import { useEffect } from 'react';

import { AuthProvider, useAuth } from './lib/auth-context.js';
import { useRoute } from './lib/route.js';
import { HomeRoute } from './routes/HomeRoute.js';
import { LoginRoute } from './routes/LoginRoute.js';
import { RegisterRoute } from './routes/RegisterRoute.js';
import { ForgotPasswordRoute } from './routes/ForgotPasswordRoute.js';
import { ResetPasswordRoute } from './routes/ResetPasswordRoute.js';
import { VerifyEmailRoute } from './routes/VerifyEmailRoute.js';
import { PostsRoute } from './routes/PostsRoute.js';
import { SavedPostsRoute } from './routes/SavedPostsRoute.js';
import { SavedPostDetailRoute } from './routes/SavedPostDetailRoute.js';
import { CarouselRoute } from './routes/CarouselRoute.js';
import { AnalysesRoute } from './routes/AnalysesRoute.js';
import { AnalysisDetailRoute } from './routes/AnalysisDetailRoute.js';
import { AnalysisCompareRoute } from './routes/AnalysisCompareRoute.js';
import { SecurityRoute } from './routes/SecurityRoute.js';
import { CadenceRoute } from './routes/CadenceRoute.js';
import { AdminSuggestionsRoute } from './routes/AdminSuggestionsRoute.js';

const PUBLIC_ROUTES = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify',
]);

const AUTH_ONLY_ROUTES = new Set(['/login', '/register']);

export default function App() {
  return (
    <AuthProvider>
      <Router />
    </AuthProvider>
  );
}

function isPrivateMatch(path: string): boolean {
  return PUBLIC_ROUTES.has(path) ? false : true;
}

function Router() {
  const { user, loading } = useAuth();
  const { path, navigate, query } = useRoute();

  useEffect(() => {
    if (loading) return;

    if (!user && isPrivateMatch(path)) {
      navigate('/login');
      return;
    }

    if (user && AUTH_ONLY_ROUTES.has(path)) {
      navigate('/');
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, path]);

  if (loading) return <BootSplash />;

  switch (path) {
    case '/login':
      return <LoginRoute />;
    case '/register':
      return <RegisterRoute />;
    case '/forgot-password':
      return <ForgotPasswordRoute />;
    case '/reset-password':
      return <ResetPasswordRoute />;
    case '/verify':
      return <VerifyEmailRoute />;
    case '/posts':
      if (!user) return <BootSplash />;
      return <PostsRoute />;
    case '/posts/saved':
      if (!user) return <BootSplash />;
      return <SavedPostsRoute />;
    case '/analises':
      if (!user) return <BootSplash />;
      return <AnalysesRoute />;
    case '/seguranca':
      if (!user) return <BootSplash />;
      return <SecurityRoute />;
    case '/cadencia':
      if (!user) return <BootSplash />;
      return <CadenceRoute />;
    case '/admin/sugestoes':
      if (!user) return <BootSplash />;
      return <AdminSuggestionsRoute />;
    case '/comparar': {
      if (!user) return <BootSplash />;
      const aId = query.a;
      const bId = query.b;
      if (!aId || !bId) {
        navigate('/analises');
        return <BootSplash />;
      }
      return <AnalysisCompareRoute aId={aId} bId={bId} />;
    }
    default: {
      if (!user) return <BootSplash />;
      if (path.startsWith('/posts/saved/')) {
        const id = path.slice('/posts/saved/'.length);
        if (id) return <SavedPostDetailRoute id={id} />;
      }
      if (path.startsWith('/analises/')) {
        const id = path.slice('/analises/'.length);
        if (id) return <AnalysisDetailRoute id={id} />;
      }
      if (path === '/carousel' || path.startsWith('/carousel/')) {
        // Aceita base64 do carrossel via ?d=... (passado pelo DraftCard).
        const data = query.d;
        if (data) return <CarouselRoute encodedData={data} />;
      }
      return <HomeRoute />;
    }
  }
}

function BootSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-paper)]">
      <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        Carregando...
      </p>
    </div>
  );
}
